import { btnSecundario, chip, money, pesos } from '../../components/ui/estilos';
import type { Cobro, Metodo } from './useCobro';
import PointCobro from './PointCobro';

const BILLETES = [200, 500, 1000];
const n = (v: string | undefined) => parseFloat(v || '0') || 0;

const accion: React.CSSProperties = {
  flex: 1, minWidth: 100, height: 40, padding: '0 10px', borderRadius: 9, border: '1px solid var(--line-strong)',
  background: 'var(--surface)', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: 'var(--ink)', whiteSpace: 'nowrap',
};
const campoMonto = (activo: boolean): React.CSSProperties => ({
  width: 110, flex: 'none', height: 40, padding: '0 10px', borderRadius: 8, fontSize: 17, fontWeight: 700, textAlign: 'right',
  border: `1px solid ${activo ? 'var(--brand-texto)' : 'var(--line-strong)'}`, background: 'var(--surface)', color: 'var(--ink)',
});
const etiqueta: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: '.04em', color: 'var(--muted)', textTransform: 'uppercase' };
// Acciones de abajo: botones 2 × 2.
const secundaria: React.CSSProperties = { ...btnSecundario, height: 44, fontSize: 15, padding: '0 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const enlace: React.CSSProperties = { background: 'none', border: 'none', padding: 0, fontSize: 15, fontWeight: 700, color: 'var(--muted)', textDecoration: 'underline', cursor: 'pointer' };

interface Props {
  c: Cobro;
  metodos: Metodo[];
  pctsPropina: number[];
  encabezado: { label: string; sub: string; estado: { label: string; bg: string; fg: string }; abierta: boolean };
  subcuentas?: { id: string; etiqueta: string; total: number; pagada: boolean; activa: boolean; onClick: () => void }[];
  onDescuento: () => void; onDividir?: () => void; onUnir: () => void; onPrecuenta: () => void;
  onEditarProductos: () => void; onCargo: () => void; onCajon: () => void; onCancelar: () => void;
  onQuitarCargo: (id: string) => void;
  accionesAparte?: boolean; // en Cuentas van fijas abajo de la lista
}

export default function PanelCobro({ c, metodos, pctsPropina, encabezado: e, subcuentas, ...a }: Props) {
  const cta = c.cuenta;
  const hayEfectivo = metodos.some(m => m.value === 'efectivo');
  const saldo = c.ingresado === 0 ? null : c.suficiente
    ? (c.cambio > 0.5 ? { label: 'Cambio', v: c.cambio, color: 'var(--ok)' } : { label: 'Cubierto', v: 0, color: 'var(--ok)' })
    : { label: 'Falta', v: c.faltante, color: 'var(--danger)' };

  return (
    <div style={{ maxWidth: 1040, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{e.label}</h1>
          <span style={{ fontSize: 15, color: 'var(--muted)' }}>{e.sub}</span>
        </div>
        <span style={{ ...chip(e.estado.bg, e.estado.fg), padding: '4px 12px' }}>{e.estado.label}</span>
      </div>

      {subcuentas && subcuentas.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {subcuentas.map(s => (
            <button key={s.id} onClick={s.onClick} disabled={s.pagada} aria-pressed={s.activa}
              style={{ ...btnSecundario, height: 40, fontSize: 15, background: s.activa ? 'var(--strong)' : 'var(--surface)', color: s.activa ? 'var(--on-strong)' : 'var(--ink)', opacity: s.pagada ? .45 : 1 }}>
              Mesa {s.etiqueta} · {s.pagada ? 'pagada' : pesos(s.total)}
            </button>
          ))}
        </div>
      )}

      {e.abierta && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--well)', fontSize: 15, lineHeight: 1.35 }}>
          Sigue abierta en el salón. Se cobra cuando el mesero pida la cuenta, o ahora si el cliente ya se va.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, alignItems: 'start' }}>
        {/* ── Productos ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '8px 14px 12px' }}>
          {cta.items.map((i: any, idx: number) => {
            const d = n(c.descItems[i.id]);
            const ronda = (cta.folios_count ?? 1) > 1 && (idx === 0 || cta.items[idx - 1].folio_num !== i.folio_num);
            return (
              <div key={i.id}>
                {ronda && <div style={{ ...etiqueta, padding: '10px 0 2px' }}>Pedido {i.folio_num}</div>}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
                  <span style={{ fontWeight: 700, width: 28, fontSize: 16 }}>{i.cantidad}×</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16 }}>{i.producto}</div>
                    {i.notas && <div style={{ fontSize: 14, color: 'var(--muted)' }}>{i.notas}</div>}
                    {d > 0 && <div style={{ fontSize: 14, color: 'var(--danger)', fontWeight: 700 }}>−{pesos(d)}{d >= i.importe - 0.01 ? ' · cortesía' : ''}</div>}
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, textAlign: 'right' }}>
                    {d > 0 && <span style={{ display: 'block', fontSize: 13, color: 'var(--faint)', textDecoration: 'line-through', fontWeight: 400 }}>{pesos(i.importe)}</span>}
                    {pesos(i.importe - d)}
                  </span>
                </div>
              </div>
            );
          })}
          {cta.cargos_extra?.map((g: any) => (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line-2)', fontSize: 16 }}>
              <span style={{ flex: 1 }}>Cargo: {g.concepto}</span>
              <span style={{ fontWeight: 700 }}>{pesos(parseFloat(g.monto))}</span>
              <button onClick={() => a.onQuitarCargo(g.id)} aria-label={`Quitar cargo ${g.concepto}`} style={{ ...btnSecundario, height: 32, padding: '0 10px', fontSize: 14 }}>✕</button>
            </div>
          ))}
          <Linea k="Subtotal" v={pesos(cta.subtotal)} />
          <Linea k="IVA" v={pesos(cta.iva)} />
          {c.montoDescuento > 0 && <Linea k={`Descuento${c.autorizadoPor ? ` (aut. ${c.autorizadoPor})` : ''}`} v={`−${pesos(c.montoDescuento)}`} color="var(--danger)" />}
          {c.totalPropina > 0 && <Linea k="Propina" v={pesos(c.totalPropina)} />}
        </div>

        {/* ── Cobro ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button onClick={a.onDescuento} style={accion}>Descuento{c.autorizadoPor ? '' : ' 🔒'}</button>
            {a.onDividir && <button onClick={a.onDividir} style={accion}>{cta.dividida ? 'División' : 'Dividir'}</button>}
            <button onClick={a.onUnir} style={accion}>Unir</button>
            <button onClick={a.onPrecuenta} style={accion}>Pre-cuenta</button>
          </div>

          <div style={{ padding: 10, borderRadius: 12, background: 'var(--well)', textAlign: 'center' }}>
            <div style={etiqueta}>Total a cobrar</div>
            {c.montoDescuento > 0 && <div style={{ fontSize: 14, color: 'var(--faint)', textDecoration: 'line-through' }}>{pesos(cta.total)}</div>}
            <div style={{ fontSize: 30, fontWeight: 700 }}>{pesos(c.totalACobrar)}</div>
            {c.redondeo !== 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Redondeo {c.redondeo > 0 ? '+' : '−'}{pesos(Math.abs(c.redondeo))}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {metodos.map(m => {
              const activo = n(c.montos[m.value]) > 0;
              return (
                <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10, border: `1px solid ${activo ? 'var(--brand-texto)' : 'var(--line)'}`, background: 'var(--surface)' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.label}</span>
                  <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={c.montos[m.value] || ''}
                    onChange={ev => c.setMonto(m.value, ev.target.value)} onFocus={ev => ev.target.select()} className="sin-flechas" style={campoMonto(activo)} />
                </label>
              );
            })}
            {hayEfectivo && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => c.setMontos({ efectivo: c.totalACobrar.toFixed(2) })} style={{ ...accion, height: 36, minWidth: 0 }}>Exacto</button>
                {BILLETES.map(b => (
                  <button key={b} onClick={() => c.setMontos({ efectivo: b.toFixed(2) })} disabled={b < c.totalACobrar}
                    style={{ ...accion, height: 36, minWidth: 0, opacity: b < c.totalACobrar ? .4 : 1 }}>{money(b)}</button>
                ))}
              </div>
            )}
            {saldo && (
              <div aria-live="polite" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, padding: '4px 2px' }}>
                <span>{saldo.label}</span><span style={{ color: saldo.color }}>{pesos(saldo.v)}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={etiqueta}>Propina (opcional)</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {pctsPropina.map(p => (
                  <button key={p} onClick={() => c.aplicarPctPropina(p)} style={{ height: 32, padding: '0 10px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer', background: 'var(--well)', border: 'none', color: 'var(--ink)' }}>{p}%</button>
                ))}
                {c.totalPropina > 0 && <button onClick={c.quitarPropina} style={{ ...enlace, fontSize: 14, marginLeft: 4 }}>Quitar</button>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 8 }}>
              {metodos.map(m => (
                // Mismo aspecto que los montos de pago: el recuadro entero es el campo y se marca al tener propina.
                <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 12px', borderRadius: 10, cursor: 'text', background: 'var(--surface)',
                  border: `1px solid ${n(c.propinas[m.value]) > 0 ? 'var(--brand-texto)' : 'var(--line)'}` }}>
                  <span style={{ fontSize: 14, flex: 1, minWidth: 0, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</span>
                  <span aria-hidden style={{ fontSize: 16, fontWeight: 700, color: 'var(--muted)' }}>$</span>
                  {/* Flechas del teclado de 0.25 en 0.25; sin las flechitas del navegador. */}
                  <input type="number" className="sin-flechas" inputMode="decimal" min="0" step="0.25" placeholder="0" value={c.propinas[m.value] || ''}
                    aria-label={`Propina en ${m.label}`}
                    onChange={ev => c.setPropina(m.value, ev.target.value)} onFocus={ev => ev.target.select()}
                    style={{ width: 58, height: 36, padding: 0, border: 'none', outline: 'none', fontSize: 17, fontWeight: 700, textAlign: 'right', background: 'transparent', color: 'var(--ink)' }} />
                </label>
              ))}
            </div>
          </div>

          {c.error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700, fontSize: 15 }}>{c.error}</div>}

          <PointCobro key={`${cta.comanda_id}:${c.sel?.tipo === 'mesa' ? c.sel.subcuenta : ''}`} c={c} />
          <button onClick={c.pagar} disabled={!c.puedeCobrar}
            style={{ minHeight: 56, borderRadius: 12, border: 'none', fontSize: 18, fontWeight: 700, cursor: c.puedeCobrar ? 'pointer' : 'not-allowed', background: 'var(--brand)', color: 'var(--on-brand)', opacity: c.puedeCobrar ? 1 : .4 }}>
            {c.pagando ? 'Registrando…' : `Cobrar ${pesos(c.totalACobrar)}`}
          </button>

          {!a.accionesAparte && <AccionesCuenta {...a} />}
        </div>
      </div>
    </div>
  );
}

function Linea({ k, v, color = 'var(--muted)' }: { k: string; v: string; color?: string }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 15, color }}><span>{k}</span><span>{v}</span></div>;
}

type Acciones = { onEditarProductos: () => void; onCargo: () => void; onCajon: () => void; onCancelar: () => void };
export function AccionesCuenta(a: Acciones) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <button onClick={a.onEditarProductos} style={secundaria}>Editar productos</button>
      <button onClick={a.onCargo} style={secundaria}>Cargo extra</button>
      <button onClick={a.onCajon} style={secundaria}>Abrir cajón</button>
      <button onClick={a.onCancelar} style={{ ...secundaria, color: 'var(--danger)', borderColor: 'var(--danger)' }}>Cancelar cuenta</button>
    </div>
  );
}
